import { supabase } from '@/integrations/supabase/client';

/**
 * The single source of truth for "which id do I filter/write owner-scoped data with".
 *
 * ## Why this exists
 *
 * ~91 tables carry RLS of the form `user_id = current_account_owner_id()`, so their
 * rows are stamped with the ACCOUNT OWNER's id — never the raw session id. For an
 * account owner the two are identical, which is exactly why this bug is invisible in
 * development: `.eq('user_id', user.id)` reads as obviously correct and works
 * perfectly until a shared-account MEMBER logs in, at which point it silently
 * matches zero rows. No error, no empty-state — just a page that renders nothing.
 *
 * That is not hypothetical. It has now shipped independently in at least five
 * places (brand_kits mutations, the realtime filter, edge write paths, video
 * limits, and ~20 analytics/media read sites), and before this module there were
 * 10+ hand-rolled copies of the same `rpc('current_account_owner_id')` call with no
 * shared seam. The convention was known, written down, and applied to half the call
 * sites. A convention that holds half the time is not a convention.
 *
 * ## When to use it
 *
 * Use `resolveOwnerId()` for any id you put in a `.eq('user_id', …)` / `.eq('owner_id', …)`
 * filter, an insert, or a realtime `filter:` on an OWNER-SCOPED table.
 *
 * Do NOT use it for tables whose RLS gates on the raw `auth.uid()` (chat_sessions,
 * ai_messages, ai_sessions, usage_events and ~16 others). There, `user.id` is
 * genuinely correct and swapping it would be a real bug in the other direction.
 * Correctness is a property of (row, table) — never of the id alone.
 *
 * ## Behaviour
 *
 * Fails closed: throws if the RPC errors or returns no owner id, rather than
 * silently falling back to the raw caller id. Falling back is exactly how
 * rows get orphaned for a MEMBER account — a transient network blip must
 * surface as a visible, retryable error, never a silent tenant swap. This
 * matches `BaseService.ensureAuthenticatedOwner()`'s model; the two used to
 * disagree, which was the actual bug (2026-08-15 dup-unifier finding).
 *
 * The thrown message deliberately avoids the substring "unauthorized" —
 * `src/config/queryClient.ts`'s default retry logic disables retries when an
 * error message contains that word, and this failure mode (a transient RPC
 * hiccup) is exactly the case retries should absorb automatically for
 * `useQuery`/`useMutation` callers.
 */
export async function resolveOwnerId(_rawUserId?: string): Promise<string> {
  const { data, error } = await supabase.rpc('current_account_owner_id');
  if (error) {
    throw new Error(`Account owner lookup failed: ${error.message}`);
  }
  if (typeof data !== 'string' || data.length === 0) {
    throw new Error('Account owner lookup returned no owner');
  }
  return data;
}

/** TanStack Query key factory — owner-scoped queries must key on the RESOLVED id,
 *  or two members of one account would keep separate caches of identical data. */
export function ownerScopedKey(base: readonly unknown[], ownerId: string | undefined) {
  return [...base, ownerId] as const;
}
